import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';

export interface SocialLinks {
  instagram?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  pinterest?: string;
  website?: string;
}

export interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  handle: string | null;
  avatar_url: string | null;
  cover_image_url: string | null;
  bio: string | null;
  social_links: SocialLinks | null;
  country: string | null;
  currency: string | null;
  gender: string | null;
  age_range: string | null;
  clothing_size: string | null;
  style_dna: Record<string, any> | null;
  style_preferences: Record<string, any> | null;
  hide_quick_try: boolean;
  include_secondhand: boolean;
  created_at: string;
  updated_at: string;
}

// Helper to convert DB response to Profile type
function toProfile(data: any): Profile {
  return {
    id: data.id,
    user_id: data.user_id,
    display_name: data.display_name,
    handle: data.handle,
    avatar_url: data.avatar_url,
    cover_image_url: data.cover_image_url,
    bio: data.bio,
    social_links: data.social_links as SocialLinks | null,
    country: data.country,
    currency: data.currency,
    gender: data.gender,
    age_range: data.age_range,
    clothing_size: data.clothing_size,
    style_dna: data.style_dna as Record<string, any> | null,
    style_preferences: data.style_preferences as Record<string, any> | null,
    hide_quick_try: data.hide_quick_try ?? false,
    include_secondhand: data.include_secondhand ?? true,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
}

async function fetchOrCreateProfile(userId: string): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    // Create profile if it doesn't exist
    const { data: newProfile, error: createError } = await supabase
      .from('profiles')
      .insert({ user_id: userId })
      .select()
      .single();

    if (createError) throw createError;
    return toProfile(newProfile);
  }

  return toProfile(data);
}

export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: loading, refetch } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: () => fetchOrCreateProfile(user!.id),
    enabled: !!user,
    staleTime: 1000 * 60 * 10, // 10 minutes - profiles rarely change
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (updates: { 
      display_name?: string; 
      handle?: string;
      avatar_url?: string; 
      cover_image_url?: string; 
      bio?: string | null; 
      social_links?: SocialLinks; 
      country?: string; 
      currency?: string;
      gender?: string | null;
      age_range?: string | null;
      clothing_size?: string | null;
      style_dna?: Record<string, any> | null;
      hide_quick_try?: boolean;
      include_secondhand?: boolean;
    }) => {
      if (!user) throw new Error('Not authenticated');

      // Convert SocialLinks to Json for database
      const dbUpdates: Record<string, any> = { ...updates };
      if (updates.social_links) {
        dbUpdates.social_links = updates.social_links as unknown as Json;
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(dbUpdates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return { profile: toProfile(data), updates };
    },
    onSuccess: ({ profile: data, updates }) => {
      queryClient.setQueryData(['profile', user?.id], data);

      // Update persona signals for identity-related changes (fire and forget)
      const identityFields = ['gender', 'age_range', 'clothing_size', 'country', 'currency', 'display_name'];
      const hasIdentityUpdate = identityFields.some(field => field in updates);
      
      if (hasIdentityUpdate) {
        supabase.functions.invoke('update-persona-signal', {
          body: {
            type: 'profile_update',
            data: {
              gender: updates.gender,
              age_range: updates.age_range,
              clothing_size: updates.clothing_size,
              country: updates.country,
              currency: updates.currency,
              display_name: updates.display_name,
            },
          },
        }).catch(console.error);
      }

      // Update persona with style_dna changes
      if (updates.style_dna) {
        supabase.functions.invoke('update-persona-signal', {
          body: {
            type: 'style_dna',
            data: { style_dna: updates.style_dna },
          },
        }).catch(console.error);
      }
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    },
  });

  const uploadAvatar = useCallback(async (file: File) => {
    if (!user) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      await updateProfileMutation.mutateAsync({ avatar_url: publicUrl });
      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
      throw error;
    }
  }, [user, updateProfileMutation]);

  const uploadCover = useCallback(async (file: File) => {
    if (!user) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/cover_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      await updateProfileMutation.mutateAsync({ cover_image_url: publicUrl });
      return publicUrl;
    } catch (error) {
      console.error('Error uploading cover:', error);
      toast.error('Failed to upload cover image');
      throw error;
    }
  }, [user, updateProfileMutation]);

  return { 
    profile: profile ?? null, 
    loading, 
    updateProfile: updateProfileMutation.mutateAsync, 
    uploadAvatar, 
    uploadCover, 
    refetch 
  };
}
